/*
 *
 * Dashboard
 *
 */

import React from "react";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { setTitle, setBreadcrumb, delayedDispatch, updateCrumb, setLoader } from "store/actions";
import { ContentWrapper, Icon, Table, WorksheetCard } from "components";
import { Row, Col, Card, notification, Modal, Input, Select } from "antd";
import { push } from "connected-react-router";
import { Line } from "@ant-design/charts";
import { Column } from "@ant-design/plots";
import moment from "moment";
import { DateTime } from "luxon";
import xlsx from "json-as-xlsx";

import Strings from "utils/strings";
import "./styles.scss";
import { API, Endpoints } from "utils/api";
import { translate } from "utils/utils";

export class Dashboard extends React.Component<any, any> {
	constructor(props: any) {
		super(props);

		this.state = {
			hasUnsavedFields: true,
			startDate: moment().startOf("month"),
			endDate: moment().endOf("day"),
			language: "pt",
			data: {
				kmStatistics: [],
				hoursStatistics: [],
				fuelStatistics: [],
				averageConsumptionStatistics: []
			},
			vehicleAverageConsumptionStatistics: [],
			staffs: [],
			staff: null,
			vehicles: [],
			vehicle: null,
			customers: [],
			customer: null,
			cards: {
				workValues: {
					title: Strings.dashboard.workValues,
					icon: "expenses",
					type: "workValues",
					value: 0,
					isMonetary: true
				},
				fuelValue: {
					title: Strings.dashboard.fuelUse,
					icon: "expenses",
					type: "fuelValues",
					value: 0,
					isMonetary: true
				},
				costsUse: {
					title: Strings.dashboard.costsUse,
					icon: "expenses",
					type: "costsValues",
					value: 0,
					isMonetary: true
				},
				extraWorksUse: {
					title: Strings.dashboard.extraWorksUse,
					icon: "expenses",
					type: "extraWorksValues",
					value: 0,
					isMonetary: true
				},
				kmUse: {
					title: Strings.dashboard.kmUse,
					icon: "truck",
					type: "kmValues",
					value: 0,
					isMonetary: false
				},
				hoursUse: {
					title: Strings.dashboard.hoursUse,
					icon: "speedometer",
					type: "hoursValues",
					value: 0,
					isMonetary: false
				},
				
				fuelLiters: {
					title: Strings.dashboard.fuelLiters,
					icon: "truck",
					type: "fuelLiters",
					value: 0,
					isMonetary: false
				},
			},
			worksheet: [],
			vehiclesNearReview: [],
			costTypes: [],
			showAddCostModal: false,
			tempNewCost: null
		};

		this.handleSelect = this.handleSelect.bind(this);
	}

	componentDidMount() {
		const { dispatch } = this.props;

		dispatch(setTitle(Strings.sidebar.dashboard));

		delayedDispatch(
			setBreadcrumb(() => {
				const { startDate, endDate } = this.state;
				const { user } = this.props;

				const actions = [] as Array<any>;
				if (user?.role === "driver" || user?.role === "helper" || user?.role === "mechanic") {
					actions.push({
						type: "datePicker",
						text: Strings.dashboard.period,
						dates: [startDate, endDate],
						onChange: this.handleSelect,
						className: "fixedPicker"
					});
				} else {
					actions.push(
						{
							type: "select",
							value: this.state.staff,
							options: this.state.staffs?.map((entry: any) => ({
								text: entry.name,
								value: entry._id
							})),
							allowClear: true,
							showSearch: true,
							onChange: (staff: any) => {
								this.setState(
									{
										staff
									},
									() => this.getData(true)
								);
							},
							placeholder: Strings.staff.selectStaff,
							minWidth: 180
						},
						{
							type: "select",
							separator: "left",
							value: this.state.vehicle,
							options: this.state.vehicles?.map((entry: any) => ({
								text: entry.licensePlate,
								value: entry._id
							})),
							allowClear: true,
							showSearch: true,
							onChange: (vehicle: any) => {
								this.setState(
									{
										vehicle
									},
									() => {
										this.getData(true);
										// Load vehicle consumption average if vehicle is selected
										if (vehicle) {
											this.getVehicleConsumptionAverage();
										} else {
											this.setState({ vehicleAverageConsumptionStatistics: [] });
										}
									}
								);
							},
							placeholder: Strings.dashboard.selectVehicle,
							minWidth: 180
						},
						{
							type: "label",
							text: Strings.dashboard.selectVehicleNumber,
							separator: "left",
							value: this.state.vehicleNumber,
							onChange: (e: any) => {
								this.setState(
									{
										vehicleNumber: e.target.value
									},
									() => this.getData()
								);
							},
							minWidth: 180
						},
						{
							type: "select",
							separator: "left",
							value: this.state.customer,
							options: this.state.customers?.map((entry: any) => ({
								text: entry.name,
								value: entry._id
							})),
							allowClear: true,
							showSearch: true,
							onChange: (customer: any) => {
								this.setState(
									{
										customer
									},
									() => this.getData(true)
								);
							},
							placeholder: Strings.dashboard.selectCustomer,
							minWidth: 180
						},
						{
							type: "datePicker",
							separator: "left",
							text: Strings.dashboard.period,
							dates: [startDate, endDate],
							onChange: this.handleSelect,
							className: "fixedPicker"
						}
					);
				}

				return { actions };
			})
		);

		this.getData();
	}

	componentDidUpdate() {
		const { dispatch } = this.props;
		dispatch(updateCrumb());
	}

	handleSelect(dates: any) {
		const newDates = [];
		if (dates && dates[0]) {
			newDates.push(moment(dates[0]).startOf("day"));
		}

		if (dates && dates[1]) {
			newDates.push(moment(dates[1]).endOf("day"));
		}

		this.setState(
			{
				startDate: newDates[0],
				endDate: newDates[1]
			},
			() => {
				this.getData(true);
				// Reload vehicle consumption average if vehicle is selected
				if (this.state.vehicle) {
					this.getVehicleConsumptionAverage();
				}
			}
		);
	}

	getData = async (notToWait: any = false) => {
		const { startDate, endDate, cards, staff, vehicle, customer, vehicleNumber } = this.state;
		const { dispatch, user } = this.props;


		setTimeout(
			async () => {
				dispatch(setLoader(true));

				try {
					if (user?.role === "driver" || user?.role === "helper" || user?.role === "mechanic") {
						const body = {
							startDate: startDate.format("DD/MM/yyyy"),
							endDate: endDate.format("DD/MM/yyyy"),
						};

						const [response, finishedWorksResponse, costTypesResponse] = await Promise.all([
							API.get({
								url: Endpoints.uriWorksheets()
							}),
							API.post({
								url: Endpoints.uriAnalytics(),
								data: body
							}),
							API.get({
								url: Endpoints.uriExpensesTypes()
							})
						]);

						if (response.ok && finishedWorksResponse?.ok) {
							const { worksheet = [] } = response.data.results || {};
							const finishedWorksResults = finishedWorksResponse.data.results || {};
							const { finishedWorksResume = [] } = finishedWorksResults;
							const vehiclesNearReview = finishedWorksResults.vehiclesNearReview || [];
							const costTypes = costTypesResponse?.ok ? (costTypesResponse.data?.results?.costType || []) : [];

							this.setState({ worksheet, finishedWorksResume, vehiclesNearReview, costTypes });
						} else {
							notification.error({
								message: Strings.serverErrors.title,
								description: response.data?.message || Strings.serverErrors.wentWrong,
								placement: "bottomRight",
								duration: 5
							});
						}
					} else {
						const body = {
							startDate: startDate.format("DD/MM/yyyy"),
							endDate: endDate.format("DD/MM/yyyy"),
							staff,
							vehicle,
							customer,
							vehicleNumber,
						};

						const [response, worksheetResponse, costTypesResponse] = await Promise.all([
							API.post({
								url: Endpoints.uriAnalytics(),
								data: body
							}),
							API.get({
								url: Endpoints.uriWorksheets()
							}),
							API.get({
								url: Endpoints.uriExpensesTypes()
							})
						]);

						if (response?.ok && worksheetResponse?.ok) {
							const results = response.data.results || {};
							const { analytics, staffs, vehicles, customers } = results;
							const vehiclesNearReview = results.vehiclesNearReview || [];
							const { worksheet = [] } = worksheetResponse.data.results || {};

							let diff = endDate.diff(startDate, "days") + 1;
							const format = diff > 90 ? "MM/yyyy" : "DD/MM/yyyy";

							const kmStatistics = analytics?.kmValues?.byDate?.map((elem: number, index: number) => ({
								day: moment(startDate)
									.add(index, diff > 90 ? "months" : "days")
									.format(format),
								name: Strings.dashboard.kmDetails,
								value: elem
							}));

							const hoursStatistics = analytics?.hoursValues?.byDate?.map((elem: number, index: number) => ({
								day: moment(startDate)
									.add(index, diff > 90 ? "months" : "days")
									.format(format),
								name: Strings.dashboard.hoursDetails,
								value: elem
							}));

							const fuelStatistics = analytics?.fuelValues?.byDate?.map((elem: number, index: number) => ({
								day: moment(startDate)
									.add(index, diff > 90 ? "months" : "days")
									.format(format),
								name: Strings.worksheets.amount,
								value: elem
							}));
							fuelStatistics.push(
								...analytics.fuelValues.byDateLiters.map((elem: number, index: number) => ({
									day: moment(startDate)
										.add(index, diff > 90 ? "months" : "days")
										.format(format),
									name: Strings.worksheets.liters,
									value: elem
								}))
							);

							const costsStatistics = analytics?.costsValues?.byDate?.map((elem: number, index: number) => ({
								day: moment(startDate)
									.add(index, diff > 90 ? "months" : "days")
									.format(format),
								name: Strings.dashboard.costsDetails,
								value: elem
							}));
							const workValuesStatistics = analytics?.workValues?.byDate?.map((elem: number, index: number) => ({
								day: moment(startDate)
									.add(index, diff > 90 ? "months" : "days")
									.format(format),
								name: Strings.dashboard.workValues,
								value: elem
							}));

							const newCards = JSON.parse(JSON.stringify(cards));

							newCards.hoursUse.value = analytics?.hoursValues?.value?.toFixed(0);
							newCards.kmUse.value = analytics?.kmValues?.value?.toFixed(2);
							newCards.fuelValue.value = analytics?.fuelValues?.value?.toFixed(2);
							newCards.fuelLiters.value = analytics?.fuelValues?.liters?.toFixed(2);
							newCards.costsUse.value = analytics?.costsValues?.value?.toFixed(2);
							newCards.extraWorksUse.value = analytics?.extraWorksValues?.value?.toFixed(2);
							newCards.workValues.value = analytics?.workValues?.value?.toFixed(2);

							const costTypes = costTypesResponse?.ok ? (costTypesResponse.data?.results?.costType || []) : [];
							
							this.setState({
								data: { kmStatistics, hoursStatistics, fuelStatistics, costsStatistics, workValuesStatistics, averageConsumptionStatistics: [] },
								cards: newCards,
								kmValues: analytics?.kmValues?.value,
								hoursValues: analytics?.hoursValues?.value,
								fuelValues: analytics?.fuelValues?.value,
								litersValues: analytics?.fuelValues?.liters,
								costsValues: analytics?.costsValues?.value,
								workValues: analytics?.workValues?.value,
								extraWorksValues: analytics?.extraWorksValues?.value,
								costsResume: analytics?.costsValues?.resume,
								fuelResume: analytics?.fuelValues?.resume,
								workValuesResume: analytics?.workValues?.resume,
								extraWorksResume: analytics?.extraWorksValues?.resume,
								revenueAnalysis: analytics?.revenueAnalysis,
								staffs,
								vehicles,
								customers,
								worksheet,
								vehiclesNearReview,
								costTypes
							}, () => {
								// Load vehicle consumption average data if vehicle is selected
								if (vehicle) {
									this.getVehicleConsumptionAverage();
								} else {
									this.setState({ vehicleAverageConsumptionStatistics: [] });
								}
							});
						} else {
							notification.error({
								message: Strings.serverErrors.title,
								description: response.data?.message || Strings.serverErrors.wentWrong,
								placement: "bottomRight",
								duration: 5
							});
						}
					}
				} catch (err) {
					console.log("Error", err);
					// notification.error({
					// 	message: Strings.serverErrors.title,
					// 	description: (err as string) || Strings.serverErrors.wentWrong,
					// 	placement: "bottomRight",
					// 	duration: 5
					// });
				}

				dispatch(setLoader(false));
			},
			notToWait ? 0 : 1000
		);

		
	}

	getVehicleConsumptionAverage = async () => {
		const { startDate, endDate, vehicle } = this.state;
		const { dispatch } = this.props;

		if (!vehicle) {
			this.setState({ vehicleAverageConsumptionStatistics: [] });
			return;
		}

		dispatch(setLoader(true));

		try {
			const body = {
				startDate: startDate.format("DD/MM/yyyy"),
				endDate: endDate.format("DD/MM/yyyy"),
				vehicle: vehicle
			};

			const response = await API.post({
				url: Endpoints.uriVehicleConsumptionAverage(),
				data: body
			});

			if (response?.ok) {
				const { chartData = [] } = response.data?.results || {};

				// Process chartData from new endpoint - always use DD/MM/yyyy format for daily bars
				const vehicleAverageConsumptionStatistics: any[] = chartData.map((item: any) => {
					const dateMoment = moment(item.date);
					const formattedDate = dateMoment.format("DD/MM/yyyy");
					
					return {
						day: formattedDate,
						value: item.value
					};
				});

				// Sort by date to ensure proper order
				vehicleAverageConsumptionStatistics.sort((a, b) => {
					return moment(a.day, "DD/MM/yyyy").valueOf() - moment(b.day, "DD/MM/yyyy").valueOf();
				});

				this.setState({ vehicleAverageConsumptionStatistics });
			}
		} catch (err) {
			console.log("Vehicle consumption average Error", err);
			this.setState({ vehicleAverageConsumptionStatistics: [] });
		}

		dispatch(setLoader(false));
	}

	async changeWorksheet() {
		const { tempWorksheet } = this.state;
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		try {
			const body = { 
				liters: tempWorksheet?.liters || 0,
				value: tempWorksheet?.value || 0,
				customerNumber: tempWorksheet?.customerNumber || '',
				vehicleNumber: tempWorksheet?.vehicleNumber || '',
				valueExtra: tempWorksheet?.valueExtra || 0,
				litersAdBlue: tempWorksheet?.litersAdBlue || 0,
				valueAdBlue: tempWorksheet?.valueAdBlue || 0,
				fullTank: tempWorksheet?.fullTank,
				km: tempWorksheet?.km || 0,
			} as any;

			const url = tempWorksheet?.costId || tempWorksheet?.fuelId ? 
				Endpoints.uriWorksheets(`values/${tempWorksheet?._id}/${tempWorksheet?.costId ? 'cost' : 'fuel'}/${tempWorksheet?.costId || tempWorksheet?.fuelId}`) :
				Endpoints.uriWorksheets(`values/${tempWorksheet?._id}`)

			const response = await API.patch({
				url: url,
				data: body
			});

			if (response.ok) {
				this.setState({ showValueModal: null, tempWorksheet: null })
				
				await this.getData();

				notification.success({
					message: Strings.worksheets.worksheets,
					description: response.data.message || "",
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	getReviewStatusColor(status: string) {
		if (status === "overdue") return "#ff4d4f"; // Vermelho para urgente
		if (status === "warning") return "#faad14"; // Amarelo/Laranja para aviso
		return "#52c41a"; // Verde para OK (caso haja outro status)
	}

	getReviewStatusText(status: string) {
		if (status === "overdue") return "Atrasado";
		if (status === "warning") return "Aviso";
		return "";
	}

	renderVehicleReviewCards() {
		const { vehiclesNearReview = [] } = this.state;
		const { user, dispatch } = this.props;

		// Só mostra para admin e mechanic
		if (user?.role !== "admin" && user?.role !== "mechanic" && user?.role !== "sysadmin") {
			return null;
		}

		if (!vehiclesNearReview || vehiclesNearReview.length === 0) return null;

		return (
			<ContentWrapper extraStyle={{ marginBottom: 20 }}>
				<Row gutter={[16, 16]}>
					{vehiclesNearReview.map((vehicle: any) => {
						const kmStatus = vehicle.kmStatus || "ok";
						const dateStatus = vehicle.dateStatus || "ok";
						const mostUrgentStatus = kmStatus === "overdue" || dateStatus === "overdue" ? "overdue" : 
												 kmStatus === "warning" || dateStatus === "warning" ? "warning" : "ok";
						
						const borderColor = this.getReviewStatusColor(mostUrgentStatus);
						const statusText = this.getReviewStatusText(mostUrgentStatus);

						return (
							<Col key={`vehicle_review_${vehicle._id}`} xs={24} sm={12} md={8} lg={6}>
								<Card
									hoverable
									onClick={() => dispatch(push(`/vehicles/${vehicle._id}`))}
									style={{
										borderLeft: `4px solid ${borderColor}`,
										cursor: "pointer"
									}}
									title={
										<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
											<span style={{ fontWeight: 600 }}>{vehicle.licensePlate}</span>
											{statusText && (
												<span style={{ 
													color: borderColor, 
													fontSize: 12, 
													fontWeight: 600,
													textTransform: "uppercase"
												}}>
													{statusText}
												</span>
											)}
										</div>
									}
								>
									<div style={{ marginBottom: 8 }}>
										<strong>{Strings.workshop.nextReviewDate}:</strong>
										<div style={{ 
											color: dateStatus === "overdue" ? "#ff4d4f" : dateStatus === "warning" ? "#faad14" : "#333",
											fontWeight: dateStatus !== "ok" ? 600 : 400
										}}>
											{vehicle.nextReviewDate ? DateTime.fromISO(vehicle.nextReviewDate).toFormat("dd/MM/yyyy") : "-"}
											{vehicle.dateDifference !== undefined && (
												<span style={{ fontSize: 12, marginLeft: 8 }}>
													({vehicle.dateDifference > 0 ? "+" : ""}{vehicle.dateDifference} dias)
												</span>
											)}
										</div>
									</div>
									<div style={{ marginBottom: 8 }}>
										<strong>{Strings.workshop.nextReviewKm}:</strong>
										<div style={{ 
											color: kmStatus === "overdue" ? "#ff4d4f" : kmStatus === "warning" ? "#faad14" : "#333",
											fontWeight: kmStatus !== "ok" ? 600 : 400
										}}>
											{vehicle.nextReviewKm ? vehicle.nextReviewKm.toLocaleString() : "-"}
											{vehicle.kmDifference !== undefined && (
												<span style={{ fontSize: 12, marginLeft: 8 }}>
													({vehicle.kmDifference > 0 ? "+" : ""}{vehicle.kmDifference} km)
												</span>
											)}
										</div>
									</div>
									{vehicle.lastReturnKm !== undefined && (
										<div style={{ fontSize: 12, color: "#666" }}>
											Último Km: {vehicle.lastReturnKm.toLocaleString()}
										</div>
									)}
								</Card>
							</Col>
						);
					})}
				</Row>
			</ContentWrapper>
		);
	}

	renderPendingWorksheets() {
		const { worksheet = [] } = this.state;
		const { user } = this.props;

		if (worksheet.length === 0) return null;

		return (
			<React.Fragment>
				<Row gutter={[20, 20]} style={{ paddingBottom: 40 }}>
					{((user?.role === "driver" || user?.role === "helper" || user?.role === "mechanic") && worksheet.length === 0 && (
						<Col key={`worksheet_${worksheet._id}`} xs={24}>
							<p>
								<strong>{Strings.worksheets.noneAvailable}</strong>
							</p>
						</Col>
					)) ||
						null}
					{worksheet.map((worksheet: any) => {
						return (
							<Col key={`worksheet_${worksheet._id}`} xs={24} md={12} xl={8}>
								<WorksheetCard worksheet={worksheet} />
							</Col>
						);
					})}
				</Row>
			</React.Fragment>
		);
	}

	renderDriverDashboard() {
		const { finishedWorksResume } = this.state;

		// isDriver: 1,

		return (
			<React.Fragment>
				{this.renderPendingWorksheets()}
				{this.renderVehicleReviewCards()}
				<ContentWrapper>
					<Table
						title={{
							icon: "speedometer",
							title: Strings.dashboard.myFinishedWorks
						}}
						data={finishedWorksResume}
						columns={[
							{
								Header: Strings.staff.type,
								id: "isDriver",
								accessor: (row: any) => row.isDriver ? Strings.staff.driver : Strings.staff.helper,
							},
							{
								Header: Strings.worksheets.number,
								id: "number",
								accessor: (row: any) => row.number
							},
							{
								Header: Strings.fields.date,
								id: "date",
								accessor: (row: any) => row.date && DateTime.fromISO(row.date).toFormat("dd/MM/yyyy")
							},
							{
								Header: Strings.vehicles.vehicle,
								id: "vehicle",
								minWidth: 50,
								accessor: (row: any) => row.vehicle || "-"
							},
							{
								Header: Strings.worksheets.exitHour,
								id: "exitHour",
								minWidth: 70,
								accessor: (row: any) =>
									(row.exitHour && DateTime.fromISO(row.exitHour).toFormat("HH:mm")) || "-"
							},
							{
								Header: Strings.worksheets.returnHour,
								id: "returnHour",
								minWidth: 70,
								accessor: (row: any) =>
									(row.returnHour && DateTime.fromISO(row.returnHour).toFormat("HH:mm")) || "-"
							},
							{
								Header: Strings.customers.customer,
								id: "customer",
								accessor: (row: any) => row.customer || '-'
							},
							{
								Header: Strings.worksheets.origin,
								id: "origin",
								accessor: (row: any) => row.origin || '-'
							},
							{
								Header: Strings.worksheets.destination,
								id: "destination",
								accessor: (row: any) => row.destination || '-'
							},
							{
								Header: Strings.worksheets.exitKm,
								id: "exitKm",
								minWidth: 50,
								accessor: (row: any) => row.exitKm || "-"
							},
							{
								Header: Strings.worksheets.returnKm,
								id: "returnKm",
								minWidth: 50,
								accessor: (row: any) => row.returnKm || "-"
							},
							{
								Header: Strings.worksheets.totalKm,
								id: "km",
								minWidth: 50,
								accessor: (row: any) => row.km || "-"
							},
						]}
						isSinglePage
						fullPage
						paginated={false}
					/>
				</ContentWrapper>
			</React.Fragment>
		);
	}

	renderExport() {
			const { showExportModal, fuelResume, startDate, endDate } = this.state;
			
			return (
				<Modal
					centered
					width="500px"
					visible={!!showExportModal}
					cancelButtonProps={{ className: "displayNone" }}
					okButtonProps={{ className: "displayNone" }}
					onCancel={() => {
						this.setState({ showExportModal: null });
					}}
					onOk={() => {
						this.setState({ showExportModal: null });
						
						let content = fuelResume
							?.sort((a: any, b: any) =>
								a.vehicle.localeCompare(b.vehicle) || DateTime.fromISO(a.date).toMillis() - DateTime.fromISO(b.date).toMillis()
							)
							.map((row: any) => ({
								vehicle: row.vehicle,
								date: DateTime.fromISO(row.date).toFormat("dd/MM/yyyy"),
								worksheetNumber: row.number,
								customer: row.customer,
								km: row.km?.toFixed(0),
								litersAdBlue: row.litersAdBlue?.toFixed(2),
								valueAdBlue: row.valueAdBlue?.toFixed(2),
								liters: row.liters?.toFixed(2),
								value: row.value?.toFixed(2),
								fullTank: row.fullTank === true ? "Sim" : row.fullTank === false ? "Não" : "-",
								notes: row.notes
							}));
				
						let data = [
							{
								sheet: "Abastecimentos",
								columns: [
									{ label: "Veículo", value: "vehicle" },
									{ label: "Data", value: "date" },
									{ label: "Número do trabalho", value: "worksheetNumber" },
									{ label: "Cliente", value: "customer" },
									{ label: "Kms", value: "km" },
									{ label: "Litros AdBlue", value: "litersAdBlue" },
									{ label: "Valor AdBlue", value: "valueAdBlue" },
									{ label: "Litros", value: "liters" },
									{ label: "Valor", value: "value" },
									{ label: "Depósito cheio", value: "fullTank" },
									{ label: "Notas", value: "notes" },
								],
								content: content,
							},
						];
				
						let settings = {
							fileName: `abastecimentos ${startDate.format("DD/MM/yyyy")} - ${endDate.format("DD/MM/yyyy")}`,
							extraLength: 3,
							writeMode: "writeFile",
							writeOptions: {},
						};
				
						xlsx(data, settings);

					}}
				>
					<div className="title">
						Deseja realizar o download para excel dos abastecimentos?
					</div>
				</Modal>
			);
		}

	renderValueModal() {
        const { showValueModal, tempWorksheet} = this.state;

		if (tempWorksheet?.fuelId) {
			return (
				<Modal
					centered
					width="500px"
					visible={!!showValueModal}
					cancelButtonProps={{ className: "displayNone" }}
					okButtonProps={{ className: "displayNone" }}
					onCancel={() => {
						this.setState({ showValueModal: null, tempWorksheet: null });
					}}
					onOk={async () => {
						this.setState({ showValueModal: null });
						await this.changeWorksheet();
					}}
				>
					<div className="title" style={{ paddingBottom: 20, paddingTop: 20 }}>
						{`Indique os valores do abastecimento da viatura ${tempWorksheet?.vehicle} no trabalho ${tempWorksheet?.number} na data ${DateTime.fromISO(tempWorksheet?.date).toFormat("dd/MM/yyyy")}`}
					</div>
					<Row gutter={[24, 24]}>
						<Col xs={12}>
							<label htmlFor="fuel_km" className="InputLabel">
								{Strings.vehicles.kms}
							</label>
							<Input
								id="fuel_km"
								type="number"
								value={tempWorksheet?.km}
								placeholder={Strings.vehicles.kms}
								onChange={(e: any) => {
									const km = e.target.value;
									this.setState((state: any) => ({ tempWorksheet: { ...state.tempWorksheet, km }}));
								}}
							/>
						</Col>
						<Col xs={12}>
							<label htmlFor="fuel_liters" className="InputLabel">
								{Strings.worksheets.liters}
							</label>
							<Input
								id="value"
								type="number"
								value={tempWorksheet?.liters}
								placeholder={Strings.worksheets.liters}
								addonAfter="L"
								onChange={(e: any) => {
									const liters = e.target.value;
									this.setState((state: any) => ({ tempWorksheet: { ...state.tempWorksheet, liters }}));
								}}
							/>
						</Col>
					</Row>
					<Row gutter={[24, 24]}>
						<Col xs={12}>
							<label htmlFor="fuel_value" className="InputLabel">
								{Strings.worksheets.amount}
							</label>
							<Input
								id="value"
								type="number"
								value={tempWorksheet?.value}
								placeholder={Strings.worksheets.amount}
								addonAfter="€"
								onChange={(e: any) => {
									const value = e.target.value;
									this.setState((state: any) => ({ tempWorksheet: { ...state.tempWorksheet, value }}));
								}}
							/>
						</Col>
					</Row>
					<Row gutter={[24, 24]}>
						<Col xs={12}>
							<label htmlFor="fuel_litersAdBlue" className="InputLabel">
								{Strings.worksheets.litersAdBlue}
							</label>
							<Input
								id="value"
								type="number"
								value={tempWorksheet?.litersAdBlue}
								placeholder={Strings.worksheets.litersAdBlue}
								addonAfter="L"
								onChange={(e: any) => {
									const litersAdBlue = e.target.value;
									this.setState((state: any) => ({ tempWorksheet: { ...state.tempWorksheet, litersAdBlue }}));
								}}
							/>
						</Col>
						<Col xs={12}>
							<label htmlFor="fuel_value" className="InputLabel">
								{Strings.worksheets.amountAdBlue}
							</label>
							<Input
								id="value"
								type="number"
								value={tempWorksheet?.valueAdBlue}
								placeholder={Strings.worksheets.amountAdBlue}
								addonAfter="€"
								onChange={(e: any) => {
									const valueAdBlue = e.target.value;
									this.setState((state: any) => ({ tempWorksheet: { ...state.tempWorksheet, valueAdBlue }}));
								}}
							/>
						</Col>
					</Row>
					<Row gutter={[24, 24]}>
						<Col xs={24}>
							<label htmlFor="fuel_fullTank" className="InputLabel">
								{Strings.worksheets.fullTank}
							</label>
							<Select
								id="fuel_fullTank"
								className="tagsSelector"
								style={{ width: "100%" }}
								placeholder={Strings.worksheets.fullTank}
								value={tempWorksheet?.fullTank === true ? "Sim" : tempWorksheet?.fullTank === false ? "Não" : undefined}
								onChange={(value: string) => {
									this.setState((state: any) => ({
										tempWorksheet: {
											...state.tempWorksheet,
											fullTank: value === "Sim"
										}
									}));
								}}
							>
								<Select.Option value="Sim">Sim</Select.Option>
								<Select.Option value="Não">Não</Select.Option>
							</Select>
						</Col>
					</Row>
				</Modal>
			);
		}

		if (tempWorksheet?.costId) {
			return (
				<Modal
					centered
					width="500px"
					visible={!!showValueModal}
					cancelButtonProps={{ className: "displayNone" }}
					okButtonProps={{ className: "displayNone" }}
					onCancel={() => {
						this.setState({ showValueModal: null, tempWorksheet: null });
					}}
					onOk={async () => {
						this.setState({ showValueModal: null });
						await this.changeWorksheet();
					}}
				>
					<div className="title" style={{ paddingBottom: 20, paddingTop: 20 }}>
					{ tempWorksheet?.costId ? 
						( <div>
							Indique o valor do custo {tempWorksheet?.cost} no trabalho {tempWorksheet?.number} na data {DateTime.fromISO(tempWorksheet?.date).toFormat("dd/MM/yyyy")}
							{tempWorksheet?.notes && (
							<div>
								<strong>NOTAS:</strong> {tempWorksheet?.notes}
							</div>
							)}
						</div> ) : 
						`Indique o valor a faturar ao cliente ${tempWorksheet?.customer} no trabalho ${tempWorksheet?.number} na data ${DateTime.fromISO(tempWorksheet?.date).toFormat("dd/MM/yyyy")}`
					}
						
					</div>
					<Col xs={24}>
						<Input
							id="value"
							type="number"
							value={tempWorksheet?.value}
							placeholder={Strings.worksheets.amount}
							addonAfter="€"
							onChange={(e: any) => {
								const value = e.target.value;
								this.setState((state: any) => ({ tempWorksheet: { ...state.tempWorksheet, value }}));
							}}
						/>
					</Col>
				</Modal>
			);
		}
        
        return (
			<Modal
				centered
				width="500px"
				visible={!!showValueModal}
				cancelButtonProps={{ className: "displayNone" }}
				okButtonProps={{ className: "displayNone" }}
				onCancel={() => {
					this.setState({ showValueModal: null, tempWorksheet: null });
				}}
				onOk={async () => {
					this.setState({ showValueModal: null });
					await this.changeWorksheet();
				}}
			>
				<div className="title" style={{ paddingBottom: 20, paddingTop: 20 }}>
					{`Indique o valor a faturar ao cliente ${tempWorksheet?.customer} no trabalho ${tempWorksheet?.number} na data ${DateTime.fromISO(tempWorksheet?.date).toFormat("dd/MM/yyyy")}`}
				</div>
				<Row gutter={[24, 24]}>
					<Col xs={12}>
						<label htmlFor="value" className="InputLabel">
							{Strings.worksheets.workValue}
						</label>
						<Input
							id="value"
							type="number"
							value={tempWorksheet?.value}
							placeholder={Strings.worksheets.workValue}
							addonAfter="€"
							onChange={(e: any) => {
								const value = e.target.value;
								this.setState((state: any) => ({ tempWorksheet: { ...state.tempWorksheet, value }}));
							}}
						/>
					</Col>
					<Col xs={12}>
						<label htmlFor="valueExtra" className="InputLabel">
							{Strings.worksheets.workValueExtra}
						</label>
						<Input
							id="valueExtra"
							type="number"
							value={tempWorksheet?.valueExtra}
							placeholder={Strings.worksheets.workValueExtra}
							addonAfter="€"
							onChange={(e: any) => {
								const valueExtra = e.target.value;
								this.setState((state: any) => ({ tempWorksheet: { ...state.tempWorksheet, valueExtra }}));
							}}
						/>
					</Col>
				</Row>
				<Row gutter={[24, 24]}>
					<Col xs={12}>
						<label htmlFor="customerNumber" className="InputLabel">
							{Strings.worksheets.customerNumber}
						</label>
						<Input
							id="customerNumber"
							value={tempWorksheet?.customerNumber}
							placeholder={Strings.worksheets.customerNumber}
							onChange={(e: any) => {
								const customerNumber = e.target.value;
								this.setState((state: any) => ({ tempWorksheet: { ...state.tempWorksheet, customerNumber }}));
							}}
						/>
					</Col>
					<Col xs={12}>
						<label htmlFor="vehicleNumber" className="InputLabel">
							{Strings.worksheets.vehicleNumber}
						</label>
						<Input
							id="vehicleNumber"
							value={tempWorksheet?.vehicleNumber}
							placeholder={Strings.worksheets.vehicleNumber}
							onChange={(e: any) => {
								const vehicleNumber = e.target.value;
								this.setState((state: any) => ({ tempWorksheet: { ...state.tempWorksheet, vehicleNumber }}));
							}}
						/>
					</Col>
				</Row>
			</Modal>
		);
    }

	renderAddCostModal() {
		const { showAddCostModal, tempNewCost, costTypes } = this.state;

		if (!showAddCostModal || !tempNewCost) return null;

		const worksheet = tempNewCost.worksheet;
		const isToll = costTypes?.find((costType: any) => costType?._id === tempNewCost?.type)?.isToll;

		return (
			<Modal
				centered
				width="500px"
				visible={!!showAddCostModal}
				title={`Adicionar custo ao trabalho ${worksheet?.number} na data ${DateTime.fromISO(worksheet?.date).toFormat("dd/MM/yyyy")}`}
				onCancel={() => {
					this.setState({ showAddCostModal: false, tempNewCost: null });
				}}
				onOk={async () => {
					this.setState({ showAddCostModal: false });
					await this.addCost();
				}}
				okText={Strings.generic.confirm}
				cancelText={Strings.generic.cancel}
			>
				<Row gutter={[24, 24]}>
					<Col xs={24}>
						<label htmlFor="new_cost_type" className="InputLabel --label-required">
							{Strings.worksheets.costType}
						</label>
						<Select
							id="new_cost_type"
							className="tagsSelector"
							style={{ width: "100%" }}
							placeholder={Strings.worksheets.costType}
							showSearch
							filterOption={(input: any, option: any) =>
								option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							value={tempNewCost?.type || null}
							onChange={(value: any) => {
								this.setState((state: any) => ({
									tempNewCost: { ...state.tempNewCost, type: value }
								}));
							}}
						>
							{costTypes.map((t: any) => (
								<Select.Option key={`type_${t._id}`} value={t._id}>
									{translate(t.description)}
								</Select.Option>
							))}
						</Select>
					</Col>
					{!isToll && (
						<Col xs={24}>
							<label htmlFor="new_cost_description" className="InputLabel --label-required">
								{Strings.fields.description}
							</label>
							<Input
								id="new_cost_description"
								value={tempNewCost?.description || ""}
								placeholder={Strings.fields.description}
								onChange={(event: any) => {
									const value = event.target.value;
									this.setState((state: any) => ({
										tempNewCost: {
											...state.tempNewCost,
											description: value
										}
									}));
								}}
							/>
						</Col>
					)}
					<Col xs={24}>
						<label htmlFor="new_cost_value" className="InputLabel">
							{Strings.worksheets.amount}
						</label>
						<Input
							id="new_cost_value"
							type="number"
							value={tempNewCost?.value != null ? tempNewCost.value : ""}
							placeholder={Strings.worksheets.amount}
							addonAfter="€"
							onChange={(e: any) => {
								const value = e.target.value;
								this.setState((state: any) => ({
									tempNewCost: {
										...state.tempNewCost,
										value
									}
								}));
							}}
						/>
					</Col>
					{isToll && (
						<>
							<Col xs={24}>
								<label htmlFor="new_cost_entry" className="InputLabel --label-required">
									{Strings.worksheets.entry}
								</label>
								<Input
									id="new_cost_entry"
									value={tempNewCost?.entry || ""}
									placeholder={Strings.worksheets.entry}
									onChange={(event: any) => {
										const value = event.target.value;
										this.setState((state: any) => ({
											tempNewCost: {
												...state.tempNewCost,
												entry: value
											}
										}));
									}}
								/>
							</Col>
							<Col xs={24}>
								<label htmlFor="new_cost_exit" className="InputLabel --label-required">
									{Strings.worksheets.exit}
								</label>
								<Input
									id="new_cost_exit"
									value={tempNewCost?.exit || ""}
									placeholder={Strings.worksheets.exit}
									onChange={(event: any) => {
										const value = event.target.value;
										this.setState((state: any) => ({
											tempNewCost: {
												...state.tempNewCost,
												exit: value
											}
										}));
									}}
								/>
							</Col>
						</>
					)}
					<Col xs={24}>
						<label htmlFor="new_cost_notes" className="InputLabel">
							{Strings.worksheets.notes}
						</label>
						<Input.TextArea
							id="new_cost_notes"
							value={tempNewCost?.notes || ""}
							placeholder={Strings.worksheets.notes}
							rows={4}
							onChange={(event: any) => {
								const value = event.target.value;
								this.setState((state: any) => ({
									tempNewCost: {
										...state.tempNewCost,
										notes: value
									}
								}));
							}}
						/>
					</Col>
				</Row>
			</Modal>
		);
	}

	async addCost() {
		const { tempNewCost } = this.state;
		const { dispatch } = this.props;

		if (!tempNewCost?.type) {
			return notification.warn({
				message: Strings.worksheets.costs,
				description: "Deve selecionar um tipo de custo.",
				placement: "bottomRight",
				duration: 5
			});
		}

		const isToll = this.state.costTypes?.find((costType: any) => costType?._id === tempNewCost?.type)?.isToll;

		if (!isToll && !tempNewCost?.description) {
			return notification.warn({
				message: Strings.worksheets.costs,
				description: "Deve preencher a descrição.",
				placement: "bottomRight",
				duration: 5
			});
		}

		if (isToll && (!tempNewCost?.entry || !tempNewCost?.exit)) {
			return notification.warn({
				message: Strings.worksheets.costs,
				description: "Deve preencher entrada e saída para portagens.",
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(true));

		try {
			const body = {
				costType: tempNewCost.type,
				description: tempNewCost.description || undefined,
				value: tempNewCost.value || 0,
				entry: tempNewCost.entry || undefined,
				exit: tempNewCost.exit || undefined,
				notes: tempNewCost.notes || undefined
			};

			const url = Endpoints.uriWorksheets(`values/${tempNewCost.worksheetId}/cost`);

			const response = await API.patch({
				url: url,
				data: body
			});

			if (response.ok) {
				this.setState({ tempNewCost: null });
				
				await this.getData();

				notification.success({
					message: Strings.worksheets.costs,
					description: response.data.message || "",
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	renderAdminDashboard() {
		const { data, cards, costsResume, fuelResume, extraWorksResume, workValuesResume, revenueAnalysis, customer, vehicleAverageConsumptionStatistics, vehicle } = this.state;

		const config = (type: string) => {
			const graphData =
				type === "kmStatistics"
					? data.kmStatistics || []
					: type === "hoursStatistics"
					? data.hoursStatistics || []
					: type === "fuelStatistics"
					? data.fuelStatistics || []
					: type === "costsStatistics"
					? data.costsStatistics || []
					: type === "averageConsumptionStatistics"
					? data.averageConsumptionStatistics || []
					: data.workValuesStatistics || []

			return {
				data: graphData,
				xField: "day",
				yField: "value",
				seriesField: "name",
				yAxis: {
					label: {
						formatter: function formatter(v: string) {
							return "".concat(v).replace(/\d{1,3}(?=(\d{3})+$)/g, function (s) {
								return "".concat(s, ",");
							});
						}
					},
					visible: true,
					min: 0
				},
				// height: 450,
				// autoFit: false,
				appendPadding: 10,
				// legend: { position: "top" },
				// renderer: "svg",
				smooth: true,
				animation: {
					appear: {
						animation: "path-in",
						duration: 2500
					}
				}
			};
		};

		const columnsCosts = [
			{
				Header: Strings.customers.customer,
				id: "customer",
				accessor: (row: any) => row.customer
			},
			{
				Header: Strings.vehicles.licensePlate,
				id: "vehicle",
				accessor: (row: any) => row.vehicle
			},
			{
				Header: Strings.worksheets.number,
				id: "number",
				accessor: (row: any) => row.number
			},
			{
				Header: Strings.fields.date,
				id: "date",
				accessor: (row: any) => row.date && DateTime.fromISO(row.date).toFormat("dd/MM/yyyy")
			},
			{
				Header: Strings.fields.category,
				id: "name",
				accessor: (row: any) => row.cost
			},
			{
				Header: Strings.fields.notes,
				id: "notes",
				accessor: (row: any) => row.notes
			},
			{
				Header: Strings.worksheets.entry,
				id: "entry",
				accessor: (row: any) => row.entry
			},
			{
				Header: Strings.worksheets.exit,
				id: "exit",
				accessor: (row: any) => row.exit
			},
			{
				Header: Strings.fields.total,
				id: "value",
				align: "right",
				accessor: (row: any) => `${row.value?.toFixed(2) || '-'} €`
			}
		] as Array<any>;

		if (customer) columnsCosts.shift();
		
		const columnsFuel = [
			{
				Header: Strings.customers.customer,
				id: "customer",
				accessor: (row: any) => row.customer
			},
			{
				Header: Strings.vehicles.vehicle,
				id: "vehicle",
				accessor: (row: any) => row.vehicle
			},
			{
				Header: Strings.worksheets.number,
				id: "number",
				accessor: (row: any) => row.number
			},
			{
				Header: Strings.fields.date,
				id: "date",
				accessor: (row: any) => row.date && DateTime.fromISO(row.date).toFormat("dd/MM/yyyy")
			},
			{
				Header: Strings.fields.description,
				id: "description",
				accessor: (row: any) => row.notes
			},
			{
				Header: Strings.vehicles.kms,
				id: "km",
				align: "right",
				accessor: (row: any) => row.km?.toFixed(0) || '-'
			},
			{
				Header: Strings.worksheets.litersAdBlue,
				id: "litersAdBlue",
				align: "right",
				accessor: (row: any) => row.litersAdBlue?.toFixed(2) || '-'
			},
			{
				Header: Strings.worksheets.amountAdBlue,
				id: "valueAdBlue",
				align: "right",
				accessor: (row: any) => `${row.valueAdBlue?.toFixed(2) || '-'} €`
			},
			{
				Header: Strings.worksheets.liters,
				id: "liters",
				align: "right",
				accessor: (row: any) => row.liters?.toFixed(2) || '-'
			},
			{
				Header: Strings.fields.total,
				id: "value",
				align: "right",
				accessor: (row: any) => `${row.value?.toFixed(2) || '-'} €`
			},
			{
				Header: Strings.worksheets.fullTank,
				id: "fullTank",
				align: "right",
				accessor: (row: any) => row.fullTank === true ? "Sim" : row.fullTank === false ? "Não" : "-",
			},
			{
				Header: Strings.vehicles.avarage,
				id: "averageConsumption",
				align: "right",
				accessor: (row: any) => row.averageConsumption ? row.averageConsumption.toFixed(2) : '-'
			},
		] as Array<any>;

		if (customer) columnsFuel.shift();
		
		const columnsWorkValues = [
			{
				Header: Strings.customers.customer,
				id: "customer",
				accessor: (row: any) => row.customer
			},
			{
				Header: Strings.vehicles.licensePlate,
				id: "vehicle",
				accessor: (row: any) => row.vehicle
			},
			{
				Header: Strings.worksheets.number,
				id: "number",
				accessor: (row: any) => row.number
			},
			{
				Header: Strings.fields.date,
				id: "date",
				accessor: (row: any) => row.date && DateTime.fromISO(row.date).toFormat("dd/MM/yyyy")
			},
			{
				Header: Strings.fields.description,
				id: "description",
				accessor: (row: any) => row.description
			},
			{
				Header: Strings.worksheets.customerNumber,
				id: "customerNumber",
				accessor: (row: any) => row.customerNumber
			},
			{
				Header: Strings.worksheets.vehicleNumber,
				id: "vehicleNumber",
				accessor: (row: any) => row.vehicleNumber
			},
			{
				Header: Strings.worksheets.workValueExtra,
				id: "valueExtra",
				align: "right",
				accessor: (row: any) => `${row.valueExtra?.toFixed(2) || '-'} €`
			},
			{
				Header: Strings.worksheets.workValue,
				id: "value",
				align: "right",
				accessor: (row: any) => `${row.value?.toFixed(2) || '-'} €`
			}
		] as Array<any>;

		if (customer) columnsWorkValues.shift();

		const columnsExtraWorks = [
			{
				Header: Strings.customers.customer,
				id: "customer",
				accessor: (row: any) => row.customer
			},
			{
				Header: Strings.worksheets.number,
				id: "number",
				accessor: (row: any) => row.number
			},
			{
				Header: Strings.fields.date,
				id: "date",
				accessor: (row: any) => row.date && DateTime.fromISO(row.date).toFormat("dd/MM/yyyy")
			},
			{
				Header: Strings.fields.description,
				id: "description",
				accessor: (row: any) => row.description
			},
			{
				Header: Strings.staff.staff,
				id: "staff",
				accessor: (row: any) => row.staff
			},
			{
				Header: Strings.fields.total,
				id: "value",
				align: "right",
				accessor: (row: any) => `${row.value?.toFixed(2) || '-'} €`
			}
		] as Array<any>;

		if (customer) columnsExtraWorks.shift();

		return (
			<React.Fragment>
				{this.renderPendingWorksheets()}
				{this.renderVehicleReviewCards()}
				<Row gutter={[24, 24]}>
					{Object.keys(cards).map((elem: any, index: number) => {
						const card = cards[elem];
						return (
							<Col key={`card_${index}`} xs={12} sm={6} md={6}>
								<ContentWrapper extraClass={`DashboardCard`}>
									<Card
										title={card.title}
										hoverable={true}
										extra={<Icon name={card.icon} className="DashboardCardIcon" />}
									>
										<p className="CardValue">{card.isMonetary ? `${card.value} €` : card.value}</p>
									</Card>
								</ContentWrapper>
							</Col>
						);
					})}
				</Row>
				<ContentWrapper extraStyle={{ padding: 20 }}>
					<div className="DashboardMainChart">
						<div className="DashboardMainChartTitle">
							<Icon name={"expenses"} />
							<span>{Strings.dashboard.workValues}</span>
						</div>
						<div className="Dashboard_Data_Charts">
							<div id="data1" className={"Dashboard_Generic_Chart$ __active"}>
								<Line {...config("workValuesStatistics")} />
							</div>
						</div>
					</div>
				</ContentWrapper>
				<ContentWrapper extraStyle={{ padding: 20 }}>
					<div className="DashboardMainChart">
						<div className="DashboardMainChartTitle">
							<Icon name={"expenses"} />
							<span>{Strings.dashboard.fuelDetails}</span>
						</div>
						<div className="Dashboard_Data_Charts">
							<div id="data1" className={"Dashboard_Generic_Chart$ __active"}>
								<Line {...config("fuelStatistics")} />
							</div>
						</div>
					</div>
				</ContentWrapper>
				<ContentWrapper extraStyle={{ padding: 20 }}>
					<div className="DashboardMainChart">
						<div className="DashboardMainChartTitle">
							<Icon name={"expenses"} />
							<span>{Strings.dashboard.costsDetails}</span>
						</div>
						<div className="Dashboard_Data_Charts">
							<div id="data1" className={"Dashboard_Generic_Chart$ __active"}>
								<Line {...config("costsStatistics")} />
							</div>
						</div>
					</div>
				</ContentWrapper>
				<ContentWrapper extraStyle={{ padding: 20 }}>
					<div className="DashboardMainChart">
						<div className="DashboardMainChartTitle">
							<Icon name={"truck"} />
							<span>{Strings.dashboard.kmDetails}</span>
						</div>
						<div className="Dashboard_Data_Charts">
							<div id="data1" className={"Dashboard_Generic_Chart$ __active"}>
								<Line {...config("kmStatistics")} />
							</div>
						</div>
					</div>
				</ContentWrapper>
				<ContentWrapper extraStyle={{ padding: 20 }}>
					<div className="DashboardMainChart">
						<div className="DashboardMainChartTitle">
							<Icon name={"speedometer"} />
							<span>{Strings.dashboard.hoursDetails}</span>
						</div>
						<div className="Dashboard_Data_Charts">
							<div id="data1" className={"Dashboard_Generic_Chart$ __active"}>
								<Line {...config("hoursStatistics")} />
							</div>
						</div>
					</div>
				</ContentWrapper>
				{vehicle && (
					<ContentWrapper extraStyle={{ padding: 20 }}>
						<div className="DashboardMainChart">
							<div className="DashboardMainChartTitle">
								<Icon name={"truck"} />
								<span>{Strings.dashboard.averageConsumptionDetails}</span>
							</div>
							<div className="Dashboard_Data_Charts">
								<div id="data1" className={"Dashboard_Generic_Chart$ __active"}>
									{vehicleAverageConsumptionStatistics.length > 0 ? (
										<Column {...{
											data: vehicleAverageConsumptionStatistics,
											xField: "day",
											yField: "value",
											xAxis: {
												label: {
													autoRotate: false,
													autoHide: false,
													autoEllipsis: false
												}
											},
											yAxis: {
												label: {
													formatter: function formatter(v: string) {
														return "".concat(v).replace(/\d{1,3}(?=(\d{3})+$)/g, function (s) {
															return "".concat(s, ",");
														});
													}
												},
												min: 0
											},
											columnWidthRatio: 0.6,
											minColumnWidth: 20,
											maxColumnWidth: 100,
											appendPadding: [10, 10, 10, 50],
											animation: {
												appear: {
													animation: "scale-in-y",
													duration: 2500
												}
											}
										}} />
									) : (
										<div style={{ padding: 20, textAlign: "center" }}>
											<p>Nenhum dado disponível para o período selecionado</p>
										</div>
									)}
								</div>
							</div>
						</div>
					</ContentWrapper>
				)}
				<ContentWrapper>
					<Table
						title={{
							icon: "expenses",
							title: Strings.dashboard.revenueAnalysis
						}}
						data={revenueAnalysis}
						columns={[
							{
								Header: Strings.customers.customer,
								id: "customer",
								accessor: (row: any) => row.customer
							},
							{
								Header: Strings.worksheets.workValueExtra,
								id: "valueExtra",
								align: "right",
								accessor: (row: any) => `${row.valueExtra?.toFixed(2) || '-'} €`
							},
							{
								Header: Strings.worksheets.workValue,
								id: "value",
								align: "right",
								accessor: (row: any) => `${row.value?.toFixed(2) || '-'} €`
							}
						]}
						fullPage
						isSinglePage
						paginated={false}
					/>
				</ContentWrapper>
				<ContentWrapper>
					<Table
						title={{
							icon: "expenses",
							title: Strings.dashboard.workValues
						}}
						data={workValuesResume}
						actions={{
							edit: (original: any) => ({
								onClick: () => {
									this.setState({ showValueModal: true, tempWorksheet: { ...original } })
								}
							}),
						}}
						columns={columnsWorkValues}
						fullPage
						paginated
					/>
				</ContentWrapper>
				<ContentWrapper>
					<Table
						title={{
							icon: "expenses",
							title: Strings.dashboard.costsResume
						}}
						data={costsResume}
						actions={{
							edit: (original: any) => ({
								onClick: () => {
									this.setState({ showValueModal: true, tempWorksheet: { ...original } })
								}
							}),
							add: (original: any) => ({
								onClick: () => {
									this.setState({ 
										showAddCostModal: true, 
										tempNewCost: { 
											worksheetId: original._id,
											worksheet: original
										} 
									})
								}
							}),
						}}
						columns={columnsCosts}
						fullPage
						paginated
					/>
				</ContentWrapper>
				<ContentWrapper>
					<Table
						title={{
							icon: "expenses",
							title: Strings.dashboard.fuelResume
						}}
						data={fuelResume}
						actions={{
							edit: (original: any) => ({
								onClick: () => {
									this.setState({ showValueModal: true, tempWorksheet: { ...original } })
								}
							}),
						}}
						headerOptions={[{
							label: Strings.generic.export,
							tooltip: Strings.generic.export,
							icon: "paper-plane",
							onClick: () => this.setState({ showExportModal: true }),
						}]}
						columns={columnsFuel}
						fullPage
						paginated
					/>
				</ContentWrapper>
				<ContentWrapper>
					<Table
						title={{
							icon: "expenses",
							title: Strings.dashboard.extraWorksUse
						}}
						data={extraWorksResume}
						columns={columnsExtraWorks}
						isSinglePage
						fullPage
						paginated={false}
					/>
				</ContentWrapper>
			</React.Fragment>
		);
	}

	render() {
		const { user } = this.props;

		return (
			<React.Fragment>
				<Helmet>
					<title>{Strings.sidebar.dashboard}</title>
					<meta name="description" content="Description of Dashboard" />
				</Helmet>
				{user?.role === "driver" || user?.role === "helper" || user?.role === "mechanic" ? this.renderDriverDashboard() : this.renderAdminDashboard()}
				{this.renderValueModal()}
				{this.renderAddCostModal()}
				{this.renderExport()}
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: any) => ({
	selectedBusiness: state.selectedBusiness,
	language: state.language,
	user: state.user
});

export default connect(mapStateToProps)(Dashboard);
